// Reine Logik der Telefonie (ohne React, ohne Twilio), damit sie mit `node --test` prüfbar ist
// (tests/logik.test.mjs). Keine Importe in dieser Datei.

/** RMS-Pegel in dBFS aus Zeitbereichs-Proben (-1..1). Digitale Stille ergibt -Infinity. */
export function rmsDbfs(proben: ArrayLike<number>): number {
  if (!proben.length) return -Infinity;
  let summe = 0;
  for (let i = 0; i < proben.length; i++) summe += proben[i] * proben[i];
  const rms = Math.sqrt(summe / proben.length);
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

/** dBFS (-100..0) auf 0..1 für eine Pegelanzeige. */
export function pegelAnteil(dbfs: number): number {
  if (!Number.isFinite(dbfs)) return 0;
  return Math.max(0, Math.min(1, (dbfs + 70) / 60));
}

export type StilleWechsel = "stille" | "ok" | null;

/**
 * Eigene Regel "Mikrofon-Stille": Pegel unter -85 dBFS für 3 s, während verbunden und nicht stumm.
 * probe() liefert "stille" beim Eintritt, "ok" beim Ende, sonst null.
 */
export class StilleErkennung {
  schwelleDb: number;
  dauerMs: number;
  seit: number | null = null;
  aktiv = false;

  constructor(schwelleDb = -85, dauerMs = 3000) {
    this.schwelleDb = schwelleDb;
    this.dauerMs = dauerMs;
  }

  probe(dbfs: number, zeitMs: number, pruefen: boolean): StilleWechsel {
    const leise = pruefen && dbfs < this.schwelleDb;
    if (!leise) {
      this.seit = null;
      if (this.aktiv) {
        this.aktiv = false;
        return "ok";
      }
      return null;
    }
    if (this.seit === null) this.seit = zeitMs;
    if (!this.aktiv && zeitMs - this.seit >= this.dauerMs) {
      this.aktiv = true;
      return "stille";
    }
    return null;
  }

  zuruecksetzen(): void {
    this.seit = null;
    this.aktiv = false;
  }
}

/** Unter diesem Pegel (dBFS) ist das Mikrofon zu leise: normal sind -20 bis -30 beim Sprechen. */
export const LEISE_SCHWELLE_DB = -50;

export type LeiseWechsel = "leise" | "ok" | null;

/**
 * „Mikrofon zu leise“: Die lauteste Spitze der letzten `fensterMs` Millisekunden liegt unter -50 dBFS (Sprechen
 * ergibt -20 bis -30). Erst nach `anlaufMs` Gesprächszeit, damit das erste „Guten Tag“ gewürdigt wird.
 * probe() liefert „leise“ beim Eintritt, „ok“ beim Ende (mit 6 dB Abstand), sonst null.
 */
export class LeisePegelErkennung {
  schwelleDb: number;
  fensterMs: number;
  anlaufMs: number;
  aktiv = false;
  private start: number | null = null;
  private proben: { t: number; db: number }[] = [];

  constructor(schwelleDb = LEISE_SCHWELLE_DB, fensterMs = 15000, anlaufMs = 8000) {
    this.schwelleDb = schwelleDb;
    this.fensterMs = fensterMs;
    this.anlaufMs = anlaufMs;
  }

  probe(dbfs: number, zeitMs: number, pruefen: boolean): LeiseWechsel {
    if (!pruefen) {
      this.start = null;
      this.proben = [];
      if (this.aktiv) {
        this.aktiv = false;
        return "ok";
      }
      return null;
    }
    if (this.start === null) this.start = zeitMs;
    this.proben.push({ t: zeitMs, db: dbfs });
    while (this.proben.length && zeitMs - this.proben[0].t > this.fensterMs) this.proben.shift();
    if (zeitMs - this.start < this.anlaufMs) return null;
    const spitze = this.proben.reduce((m, p) => Math.max(m, p.db), -Infinity);
    if (!this.aktiv && spitze < this.schwelleDb) {
      this.aktiv = true;
      return "leise";
    }
    if (this.aktiv && spitze >= this.schwelleDb + 6) {
      this.aktiv = false;
      return "ok";
    }
    return null;
  }

  zuruecksetzen(): void {
    this.start = null;
    this.proben = [];
    this.aktiv = false;
  }
}

/** Bewertung eines gemessenen Spitzenpegels (dBFS) vor dem Anruf. */
export function pegelUrteil(spitzeDb: number | null): "unbekannt" | "stille" | "leise" | "gut" {
  if (spitzeDb === null) return "unbekannt";
  if (spitzeDb < -85) return "stille";
  if (spitzeDb < LEISE_SCHWELLE_DB) return "leise";
  return "gut";
}

/** Zahl mit deutschem Tausenderpunkt: 3600 -> 3.600 */
export function zahlText(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** Kürzel aus dem Namen für die Pille: „Dilsar Altan“ -> „DA“. */
export function kuerzel(name: string | null | undefined): string {
  const teile = (name ?? "").split(/\s+/).filter(Boolean);
  if (!teile.length) return "?";
  const a = teile[0][0] ?? "";
  const b = teile.length > 1 ? (teile[teile.length - 1][0] ?? "") : (teile[0][1] ?? "");
  return (a + b).toUpperCase();
}

export type Schwere = "rot" | "gelb" | "info";

/** Klartexte zu den Twilio-SDK-Warnungen und eigenen Regeln. */
export const WARNUNGEN: Record<string, { text: string; schwere: Schwere; handy?: boolean; mikrofon?: boolean }> = {
  "mikrofon-stille": {
    text: "Dein Mikrofon sendet nichts. Headset prüfen oder auf eingebautes Mikrofon wechseln.",
    schwere: "rot",
    handy: true,
    mikrofon: true,
  },
  "constant-audio-input-level": {
    text: "Dein Mikrofon sendet nichts. Headset prüfen (Stummschalter am Headset?) oder auf eingebautes Mikrofon wechseln.",
    schwere: "rot",
    handy: true,
    mikrofon: true,
  },
  "mikrofon-leise": {
    text: "Dein Mikrofon ist zu leise (unter −50 dBFS), der Kunde hört dich kaum. Gerät in der Pille wechseln.",
    schwere: "gelb",
    mikrofon: true,
  },
  "constant-audio-output-level": { text: "Vom Kunden kommt kein Ton an.", schwere: "gelb" },
  "low-mos": {
    text: "Gesprächsqualität schlecht. Wenn ihr euch schlecht versteht: Gespräch aufs Handy holen.",
    schwere: "rot",
    handy: true,
  },
  "high-packet-loss": { text: "Leitung instabil (Paketverlust). WLAN prüfen oder Kabel nutzen.", schwere: "gelb" },
  "high-packets-lost-fraction": { text: "Leitung instabil (Paketverlust). WLAN prüfen oder Kabel nutzen.", schwere: "gelb" },
  "high-jitter": { text: "Leitung ungleichmäßig. Der Ton kann abgehackt klingen.", schwere: "gelb" },
  "high-rtt": { text: "Hohe Verzögerung. Der Kunde hört dich verspätet.", schwere: "gelb" },
  "low-bytes-sent": {
    text: "Verbindung nach außen unterbrochen: Dein Ton kommt nicht beim Kunden an.",
    schwere: "rot",
    handy: true,
  },
  "low-bytes-received": { text: "Du hörst den Kunden nicht: Verbindung gestört.", schwere: "rot", handy: true },
  "ice-connectivity-lost": { text: "Verbindung verloren. Wird wiederhergestellt …", schwere: "rot" },
  reconnecting: { text: "Verbindung wird wiederhergestellt …", schwere: "rot" },
  "mikrofon-verloren": { text: "Mikrofon getrennt. Auf das Standard-Mikrofon gewechselt.", schwere: "gelb" },
  "lautsprecher-verloren": { text: "Lautsprecher getrennt. Auf die Standard-Ausgabe gewechselt.", schwere: "gelb" },
};

export function warnungText(name: string): { text: string; schwere: Schwere; handy?: boolean; mikrofon?: boolean } {
  return WARNUNGEN[name] ?? { text: `Hinweis der Telefonie: ${name}`, schwere: "info" };
}

export const DISPOSITIONEN: Record<string, string> = {
  answered: "Gespräch",
  "no-answer": "Nicht erreicht",
  "vm-answer": "Mailbox, ohne Nachricht",
  "vm-left": "Mailbox, Nachricht hinterlassen",
  busy: "Besetzt",
  blocked: "Gesperrt",
  error: "Fehler beim Aufbau",
  abandoned: "Abgebrochen",
};

export function dispositionText(d: string | null | undefined): string {
  return d ? (DISPOSITIONEN[d] ?? d) : "unbekannt";
}

export const QUALITAET: Record<string, string> = {
  excellent: "sehr gut",
  great: "sehr gut",
  good: "gut",
  fair: "mäßig",
  degraded: "schlecht",
};

export function qualitaetText(q: string | null | undefined): string {
  return q ? (QUALITAET[q] ?? q) : "unbekannt";
}

/** 0:07, 12:45, 1:02:03 */
export function dauerText(sekunden: number | null | undefined): string {
  const s = Math.max(0, Math.floor(sekunden ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const zwei = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${zwei(m)}:${zwei(r)}` : `${m}:${zwei(r)}`;
}

/** Kalendertag in Europe/Berlin (YYYY-MM-DD). */
export function berlinTag(zeit: Date): string {
  return zeit.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
}

export function istHeute(iso: string | null | undefined, jetzt: Date = new Date()): boolean {
  if (!iso) return false;
  const t = new Date(iso);
  return !Number.isNaN(t.getTime()) && berlinTag(t) === berlinTag(jetzt);
}

export interface Probe {
  mos?: number | null;
  jitter?: number | null;
  rtt?: number | null;
  packet_loss?: number | null;
}

/** Kennzahlen eines Anrufs aus den Sekundenproben. */
export function zusammenfassen(proben: Probe[]): Record<string, number | null> {
  const werte = (k: keyof Probe) => proben.map((p) => p[k]).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const mittel = (l: number[]) => (l.length ? Math.round((l.reduce((a, b) => a + b, 0) / l.length) * 100) / 100 : null);
  const mos = werte("mos");
  return {
    mos_avg: mittel(mos),
    mos_min: mos.length ? Math.min(...mos) : null,
    jitter_max: werte("jitter").length ? Math.max(...werte("jitter")) : null,
    rtt_max: werte("rtt").length ? Math.max(...werte("rtt")) : null,
    packet_loss_avg: mittel(werte("packet_loss")),
    proben: proben.length,
  };
}

/** Nur Ziffern, *, # für das Tastenfeld. */
export function nurTasten(eingabe: string): string {
  return eingabe.replace(/[^0-9*#]/g, "").slice(0, 32);
}

// ---------------------------------------------------------------------------
// Nummer wählen: Eingabe, Einfügen, Formatierung für Deutschland
// ---------------------------------------------------------------------------

/** Längste Nummer (inkl. „+“), die das Eingabefeld annimmt */
export const NUMMER_MAX = 18;

/**
 * Macht aus Tippen oder Einfügen („0176 2408-0471“, „(07821) 12 34“, „0049 176 …“, „+49 (0) 176 …“) eine einheitliche
 * Schreibweise: führende 0 wird zu +49, 00 zu +, Leerzeichen, Klammern, Striche und die „(0)“ fallen weg.
 * Teilnummern bleiben unvollständig stehen (für das Tippen); ohne Plus und ohne führende 0 bleibt die Ziffernfolge.
 */
export function nummerNormalisieren(eingabe: string): string {
  let t = eingabe.replace(/\(0\)/g, "").replace(/[^\d+]/g, "");
  const plus = t.startsWith("+");
  t = t.replace(/\+/g, "");
  let r: string;
  if (plus) r = `+${t}`;
  else if (t.startsWith("00")) r = `+${t.slice(2)}`;
  else if (t.startsWith("0")) r = `+49${t.slice(1)}`;
  else r = t;
  if (r.startsWith("+490")) r = `+49${r.slice(4)}`; // „+49 0176 …“: die Null der Ortsangabe gehört nicht dazu
  return r.slice(0, NUMMER_MAX);
}

/** Anzeige einer normalisierten Nummer: „+49 176 24080471“, Festnetz „+49 7821 123456“, Großstadt „+49 30 123456“. */
export function nummerFormat(norm: string): string {
  if (!norm.startsWith("+")) return norm;
  const ziffern = norm.slice(1);
  if (ziffern.startsWith("49") && ziffern.length > 2) {
    const r = ziffern.slice(2);
    let vorwahl: number;
    if (/^1[5-7]/.test(r)) vorwahl = 3; // Mobilfunk
    else if (/^(30|40|69|89)/.test(r)) vorwahl = 2;
    else if (/^[2-9]\d1/.test(r)) vorwahl = 3; // Städte wie 211, 221, 761
    else vorwahl = 4;
    return r.length > vorwahl ? `+49 ${r.slice(0, vorwahl)} ${r.slice(vorwahl)}` : `+49 ${r}`;
  }
  const land = /^(420|421|43|41|31|33|34|39|44|48)(\d+)$/.exec(ziffern);
  return land ? `+${land[1]} ${land[2]}` : norm;
}

/** Wählbare Nummer („+49 …“) aus der Eingabe, sonst null (zu kurz). Ohne Plus gilt Deutschland. */
export function nummerZumWaehlen(eingabe: string): string | null {
  let n = nummerNormalisieren(eingabe);
  if (!n.startsWith("+")) n = /^49\d{9,}$/.test(n) ? `+${n}` : n ? `+49${n}` : "";
  return n.replace(/\D/g, "").length >= 7 ? n : null;
}

/** Ziffer, die eine Taste auf der Tastatur sendet (Tastenfeld im Gespräch): 0 bis 9, *, #, sonst null. */
export function tastenZeichen(key: string): string | null {
  return /^[0-9*#]$/.test(key) ? key : null;
}

// ---------------------------------------------------------------------------
// Registrierung des Twilio-Geräts für eingehende Anrufe (Runde 9, Wunsch desktop-2)
// Reine Logik: wann neu registriert wird, wie oft, wie lange gewartet wird und wann das Token erneuert werden muss.
// ---------------------------------------------------------------------------

/** Zustand des Twilio-Geräts (Device.State, kleingeschrieben); unbekannt = noch kein Gerät. */
export type GeraetState = "registered" | "registering" | "unregistered" | "destroyed" | string | null | undefined;

export type RegistrierAktion =
  /** nichts tun: schon registriert, läuft gerade oder ein Gespräch wird nicht gestört */
  | "nichts"
  /** register() genügt */
  | "registrieren"
  /** erst unregister(), dann register() (frischer WebSocket nach Ruhezustand oder Netzwechsel) */
  | "zyklus"
  /** Gerät ist zerstört: neu aufbauen (neues Token) und registrieren */
  | "aufbauen";

/**
 * Was jetzt zu tun ist. `erzwingen` = Verbindung gilt als verdächtig (Aufwachen, Netz zurück): auch ein
 * „registered“-Gerät wird neu angemeldet, weil sein WebSocket nach dem Ruhezustand tot sein kann. Läuft gerade
 * ein Gespräch oder klingelt es, wird nie angefasst.
 */
export function registrierAktion(state: GeraetState, o: { erzwingen?: boolean; belegt?: boolean } = {}): RegistrierAktion {
  if (state === "destroyed") return "aufbauen";
  if (state === "registering") return "nichts";
  if (state === "registered") return o.erzwingen && !o.belegt ? "zyklus" : "nichts";
  return "registrieren";
}

/** Wert für `crmDesktop.telefonStatus` aus einem Geräteereignis; null = nicht melden. */
export function telefonStatusFuer(ereignis: string): boolean | null {
  if (ereignis === "registered") return true;
  if (ereignis === "unregistered" || ereignis === "error") return false;
  return null;
}

/** Twilio-Fehlercodes, bei denen ein neues Token nötig ist (abgelaufen, ungültig, Signatur). */
const TOKEN_FEHLERCODES = new Set([20101, 20102, 20103, 20104, 20105, 20106, 31201, 31202, 31204, 31205, 31206]);

export function istTokenFehler(code: number | null | undefined): boolean {
  return typeof code === "number" && TOKEN_FEHLERCODES.has(code);
}

/** Token (Laufzeit `ttlSek`, geholt bei `geholtUm` in ms) ist abgelaufen oder läuft in `reserveSek` ab. */
export function tokenAbgelaufen(geholtUm: number | null, ttlSek: number | null, jetztMs: number, reserveSek = 120): boolean {
  if (geholtUm === null || ttlSek === null || !Number.isFinite(ttlSek)) return true;
  return jetztMs >= geholtUm + (ttlSek - reserveSek) * 1000;
}

/** Wartezeiten zwischen Registrier-Versuchen nach Fehlschlägen (ms); danach bleibt es beim letzten Wert. */
export const REGISTRIER_STUFEN_MS = [3000, 8000, 20000, 45000, 60000];

export function wiederholAbstandMs(fehlversuche: number): number {
  if (fehlversuche <= 0) return 0;
  return REGISTRIER_STUFEN_MS[Math.min(fehlversuche, REGISTRIER_STUFEN_MS.length) - 1];
}

export type RegistrierPlanung = { aktion: "planen"; inMs: number } | { aktion: "ignorieren"; grund: "wartet" | "laeuft" };

/**
 * Entprellt und serialisiert Anstöße zum Neu-Registrieren. Mehrere Anstöße kurz hintereinander (Aufwachen sofort
 * und nach 6 s, Netz zurück, Fehlerereignis) ergeben genau einen Versuch; während einer läuft oder ein Termin
 * aussteht, wird nichts dazugeplant. Nach Fehlschlägen wächst der Abstand (wiederholAbstandMs).
 */
export class RegistrierPlan {
  entprellMs: number;
  fehlversuche = 0;
  private wartet = false;
  private laeuft = false;
  private letzteErzwingung = -Infinity;

  constructor(entprellMs = 1500) {
    this.entprellMs = entprellMs;
  }

  /** `sofort`: ohne Entprellung (Erst-Anmeldung, Einstellung geändert). */
  anfordern(sofort = false): RegistrierPlanung {
    if (this.laeuft) return { aktion: "ignorieren", grund: "laeuft" };
    if (this.wartet) return { aktion: "ignorieren", grund: "wartet" };
    this.wartet = true;
    const nachFehlern = wiederholAbstandMs(this.fehlversuche);
    return { aktion: "planen", inMs: Math.max(nachFehlern, sofort ? 0 : this.entprellMs) };
  }

  /** Der geplante Termin ist da. false = es läuft schon ein Versuch (nicht doppelt). */
  starten(): boolean {
    this.wartet = false;
    if (this.laeuft) return false;
    this.laeuft = true;
    return true;
  }

  /** ok = registriert (oder nichts nötig): Zähler zurück. Sonst wächst der Abstand. */
  beenden(ok: boolean): void {
    this.laeuft = false;
    this.fehlversuche = ok ? 0 : this.fehlversuche + 1;
  }

  /** Das Gerät meldet „registered“. */
  registriert(): void {
    this.fehlversuche = 0;
  }

  /** Erzwungenes Neu-Anmelden höchstens alle `abstandMs` (das zweite Aufwachen nach 6 s soll es nicht wiederholen). */
  erzwingenErlaubt(jetztMs: number, abstandMs = 20000): boolean {
    if (jetztMs - this.letzteErzwingung < abstandMs) return false;
    this.letzteErzwingung = jetztMs;
    return true;
  }

  zuruecksetzen(): void {
    this.wartet = false;
    this.laeuft = false;
    this.fehlversuche = 0;
    this.letzteErzwingung = -Infinity;
  }
}

/** Wiederholt der Token-Erneuerung nach Fehlschlag (ms): 5, 15, 30, 60, 120 s, dann Schluss. */
export const TOKEN_WIEDERHOLUNG_MS = [5000, 15000, 30000, 60000, 120000];

/** Kurzfassung der Klingel-Karte für `crmDesktop.eingehend`. `nummerText` ist die angezeigte Schreibweise. */
export function eingehendMeldung(
  e: { leadName?: string | null; kontaktName?: string | null; anonym?: boolean; leadStatus?: string | null; nummer?: string | null; grund?: string | null; mehrere?: boolean },
  nummerText: string,
): { wer: string; status: string; nummer: string; grund: string } {
  return {
    wer: e.leadName ?? e.kontaktName ?? (e.anonym ? "Anonymer Anrufer" : "Unbekannte Nummer"),
    status: e.leadStatus ?? "",
    nummer: e.nummer ? nummerText : "",
    grund: e.mehrere ? "Die Nummer gehört zu mehreren Leads." : (e.grund ?? ""),
  };
}

/** Fehler von getUserMedia bzw. Twilio, bei dem der Nutzer oder das System das Mikrofon gesperrt hat. */
export function istMikrofonGesperrt(e: unknown): boolean {
  const f = e as { name?: string; code?: number } | null | undefined;
  return f?.name === "NotAllowedError" || f?.code === 31401 || f?.code === 31208;
}
